import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageIcon,
  PageDescription,
  ContentGrid,
  Card,
  CardTitle,
  CardContent,
  ActionBar,
  Button,
  SearchInput,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  StatusBadge,
  TabContainer,
  TabList,
  Tab,
  TabContent,
  fadeIn
} from '../styles/PageStyles';
import PageHeaderWithInfo from '../components/common/PageHeaderWithInfo';
import { faCodeCompare } from '@fortawesome/free-solid-svg-icons';

const FeedbackLoops = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('feedback');

  // Mock data for feedback items
  const feedbackItems = [
    {
      id: 'FB-001',
      agentName: 'Claims Processor',
      source: 'User Feedback',
      category: 'Accuracy',
      description: 'Agent incorrectly classified claim as fraudulent',
      impact: 'High',
      status: 'pending',
      dateSubmitted: '2025-03-17',
      assignedTo: 'AI Team'
    },
    {
      id: 'FB-002',
      agentName: 'Customer Service Bot',
      source: 'Automated Detection',
      category: 'Response Time',
      description: 'Response time exceeds threshold for complex queries',
      impact: 'Medium',
      status: 'in-progress',
      dateSubmitted: '2025-03-16',
      assignedTo: 'Performance Team'
    },
    {
      id: 'FB-003',
      agentName: 'Underwriting Assistant',
      source: 'Quality Audit',
      category: 'Completeness',
      description: 'Missing consideration of key risk factors',
      impact: 'High',
      status: 'implemented',
      dateSubmitted: '2025-03-15',
      assignedTo: 'Risk Team'
    },
    {
      id: 'FB-004',
      agentName: 'Document Analyzer',
      source: 'User Feedback',
      category: 'Accuracy',
      description: 'Misinterpreting handwritten content in scanned documents',
      impact: 'Medium',
      status: 'pending',
      dateSubmitted: '2025-03-17',
      assignedTo: 'Document Team'
    },
    {
      id: 'FB-005',
      agentName: 'Fraud Detector',
      source: 'Automated Detection',
      category: 'False Positives',
      description: 'High rate of false positives for certain claim types',
      impact: 'High',
      status: 'in-progress',
      dateSubmitted: '2025-03-16',
      assignedTo: 'Fraud Team'
    },
  ];

  // Mock data for improvements
  const improvements = [
    {
      id: 'IMP-001',
      title: 'Enhanced Fraud Detection Logic',
      description: 'Updated fraud detection algorithm to reduce false positives',
      agentName: 'Fraud Detector',
      category: 'Accuracy',
      impact: 'High',
      status: 'implemented',
      dateImplemented: '2025-03-15',
      performanceGain: '+12%'
    },
    {
      id: 'IMP-002',
      title: 'Response Time Optimization',
      description: 'Optimized prompt structure to reduce token usage and response time',
      agentName: 'Customer Service Bot',
      category: 'Performance',
      impact: 'Medium',
      status: 'implemented',
      dateImplemented: '2025-03-14',
      performanceGain: '+25%'
    },
    {
      id: 'IMP-003',
      title: 'Risk Factor Analysis Enhancement',
      description: 'Added comprehensive risk factor checklist to underwriting process',
      agentName: 'Underwriting Assistant',
      category: 'Completeness',
      impact: 'High',
      status: 'in-progress',
      dateImplemented: 'N/A',
      performanceGain: 'N/A'
    },
    {
      id: 'IMP-004',
      title: 'Document OCR Improvement',
      description: 'Integrated specialized handwriting recognition model',
      agentName: 'Document Analyzer',
      category: 'Accuracy',
      impact: 'Medium',
      status: 'planned',
      dateImplemented: 'N/A',
      performanceGain: 'N/A'
    },
    {
      id: 'IMP-005',
      title: 'Context Retention Enhancement',
      description: 'Improved context management for multi-turn conversations',
      agentName: 'Customer Service Bot',
      category: 'User Experience',
      impact: 'High',
      status: 'implemented',
      dateImplemented: '2025-03-10',
      performanceGain: '+18%'
    },
  ];

  // Mock data for metrics
  const metrics = [
    {
      id: 'MET-001',
      name: 'Accuracy',
      agentName: 'Claims Processor',
      baseline: '92.5%',
      current: '95.8%',
      trend: 'improving',
      lastUpdated: '2025-03-17'
    },
    {
      id: 'MET-002',
      name: 'Response Time',
      agentName: 'Customer Service Bot',
      baseline: '2.5s',
      current: '1.8s',
      trend: 'improving',
      lastUpdated: '2025-03-17'
    },
    {
      id: 'MET-003',
      name: 'False Positive Rate',
      agentName: 'Fraud Detector',
      baseline: '8.2%',
      current: '4.5%',
      trend: 'improving',
      lastUpdated: '2025-03-17'
    },
    {
      id: 'MET-004',
      name: 'Document Processing Accuracy',
      agentName: 'Document Analyzer',
      baseline: '90.1%',
      current: '89.5%',
      trend: 'declining',
      lastUpdated: '2025-03-17'
    },
    {
      id: 'MET-005',
      name: 'Risk Assessment Completeness',
      agentName: 'Underwriting Assistant',
      baseline: '85.0%',
      current: '94.2%',
      trend: 'improving',
      lastUpdated: '2025-03-17'
    },
  ];

  // Filter data based on search term
  const filteredFeedbackItems = feedbackItems.filter(item =>
    item.agentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredImprovements = improvements.filter(item =>
    item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.agentName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredMetrics = metrics.filter(metric =>
    metric.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    metric.agentName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'implemented': return 'active';
      case 'in-progress': return 'warning';
      case 'planned': return 'default';
      case 'pending': return 'default';
      case 'improving': return 'active';
      case 'declining': return 'error';
      case 'stable': return 'default';
      default: return 'default';
    }
  };

  // Get impact badge color
  const getImpactColor = (impact) => {
    switch (impact) {
      case 'High': return 'error';
      case 'Medium': return 'warning';
      case 'Low': return 'default';
      default: return 'default';
    }
  };

  return (
    <PageContainer>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >

        <PageHeaderWithInfo
          icon={faCodeCompare}
          title="Feedback Loops"
          description="Identify and provide feedback loops to agents based on past transactions.
          Track improvements, measure performance gains, and continuously enhance AI capabilities."
        >
          <div style={{ display: 'flex', gap: '1rem' }}>
            {/* <SearchInput
            placeholder="Search policies by name, ID, or tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          /> */}
            <Button>Export Report</Button>
            <Button primary>Submit Feedback</Button>
          </div>
        </PageHeaderWithInfo>
        <ContentGrid>
          <Card>
            <CardTitle>Feedback Overview</CardTitle>
            <CardContent>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                <div style={{
                  flex: '1 1 20%',
                  minWidth: '150px',
                  background: 'rgba(0, 102, 255, 0.1)',
                  borderRadius: '8px',
                  padding: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <div style={{ fontSize: '2rem', color: 'var(--primary)' }}>5</div>
                  <div style={{ textAlign: 'center', fontSize: '0.9rem' }}>New Feedback</div>
                </div>

                <div style={{
                  flex: '1 1 20%',
                  minWidth: '150px',
                  background: 'rgba(255, 204, 0, 0.1)',
                  borderRadius: '8px',
                  padding: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <div style={{ fontSize: '2rem', color: '#FFCC00' }}>2</div>
                  <div style={{ textAlign: 'center', fontSize: '0.9rem' }}>In Progress</div>
                </div>

                <div style={{
                  flex: '1 1 20%',
                  minWidth: '150px',
                  background: 'rgba(52, 199, 89, 0.1)',
                  borderRadius: '8px',
                  padding: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <div style={{ fontSize: '2rem', color: '#34C759' }}>3</div>
                  <div style={{ textAlign: 'center', fontSize: '0.9rem' }}>Implemented</div>
                </div>

                <div style={{
                  flex: '1 1 20%',
                  minWidth: '150px',
                  background: 'rgba(52, 199, 89, 0.1)',
                  borderRadius: '8px',
                  padding: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <div style={{ fontSize: '2rem', color: '#34C759' }}>+15%</div>
                  <div style={{ textAlign: 'center', fontSize: '0.9rem' }}>Performance Gain</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardTitle>Performance Trends</CardTitle>
            <CardContent>
              <div style={{ height: '150px', display: 'flex', alignItems: 'flex-end', gap: '8px', marginTop: '20px' }}>
                {[82, 85, 88, 86, 90, 93, 95].map((value, index) => (
                  <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{
                      height: `${value}px`,
                      width: '100%',
                      background: value < 85 ? '#FFCC00' : 'var(--primary)',
                      borderRadius: '4px 4px 0 0'
                    }}></div>
                    <div style={{ fontSize: '0.7rem', marginTop: '5px' }}>W{index + 1}</div>
                  </div>
                ))}
              </div>
              <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                Overall Agent Performance - Last 7 Weeks
              </div>
            </CardContent>
          </Card>
        </ContentGrid>

        <TabContainer style={{ marginTop: '1.5rem' }}>
          <TabList>
            <Tab active={activeTab === 'feedback'} onClick={() => setActiveTab('feedback')}>Feedback Items</Tab>
            <Tab active={activeTab === 'improvements'} onClick={() => setActiveTab('improvements')}>Improvements</Tab>
            <Tab active={activeTab === 'metrics'} onClick={() => setActiveTab('metrics')}>Performance Metrics</Tab>
          </TabList>

          <ActionBar>
            <SearchInput
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Button>Filter</Button>
            <Button>Sort</Button>
          </ActionBar>

          <TabContent active={activeTab === 'feedback'}>
            <Card>
              <CardTitle>Feedback Items</CardTitle>
              <Table>
                <thead>
                  <tr>
                    <TableHeader>ID</TableHeader>
                    <TableHeader>Agent</TableHeader>
                    <TableHeader>Category</TableHeader>
                    <TableHeader>Description</TableHeader>
                    <TableHeader>Source</TableHeader>
                    <TableHeader>Date Submitted</TableHeader>
                    <TableHeader>Assigned To</TableHeader>
                    <TableHeader>Impact</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {filteredFeedbackItems.map(item => (
                    <TableRow key={item.id}>
                      <TableCell>{item.id}</TableCell>
                      <TableCell>{item.agentName}</TableCell>
                      <TableCell>{item.category}</TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>{item.source}</TableCell>
                      <TableCell>{item.dateSubmitted}</TableCell>
                      <TableCell>{item.assignedTo}</TableCell>
                      <TableCell>
                        <StatusBadge status={getImpactColor(item.impact)}>
                          {item.impact}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={getStatusColor(item.status)}>
                          {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Button style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Details</Button>
                          <Button primary style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Process</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </Card>
          </TabContent>

          <TabContent active={activeTab === 'improvements'}>
            <Card>
              <CardTitle>Implemented Improvements</CardTitle>
              <Table>
                <thead>
                  <tr>
                    <TableHeader>ID</TableHeader>
                    <TableHeader>Title</TableHeader>
                    <TableHeader>Description</TableHeader>
                    <TableHeader>Agent</TableHeader>
                    <TableHeader>Category</TableHeader>
                    <TableHeader>Date Implemented</TableHeader>
                    <TableHeader>Performance Gain</TableHeader>
                    <TableHeader>Impact</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {filteredImprovements.map(item => (
                    <TableRow key={item.id}>
                      <TableCell>{item.id}</TableCell>
                      <TableCell>{item.title}</TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>{item.agentName}</TableCell>
                      <TableCell>{item.category}</TableCell>
                      <TableCell>{item.dateImplemented}</TableCell>
                      <TableCell>{item.performanceGain}</TableCell>
                      <TableCell>
                        <StatusBadge status={getImpactColor(item.impact)}>
                          {item.impact}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={getStatusColor(item.status)}>
                          {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Button style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Details</Button>
                          <Button primary style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Metrics</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </Card>
          </TabContent>

          <TabContent active={activeTab === 'metrics'}>
            <Card>
              <CardTitle>Performance Metrics</CardTitle>
              <Table>
                <thead>
                  <tr>
                    <TableHeader>ID</TableHeader>
                    <TableHeader>Metric</TableHeader>
                    <TableHeader>Agent</TableHeader>
                    <TableHeader>Baseline</TableHeader>
                    <TableHeader>Current</TableHeader>
                    <TableHeader>Last Updated</TableHeader>
                    <TableHeader>Trend</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {filteredMetrics.map(metric => (
                    <TableRow key={metric.id}>
                      <TableCell>{metric.id}</TableCell>
                      <TableCell>{metric.name}</TableCell>
                      <TableCell>{metric.agentName}</TableCell>
                      <TableCell>{metric.baseline}</TableCell>
                      <TableCell>{metric.current}</TableCell>
                      <TableCell>{metric.lastUpdated}</TableCell>
                      <TableCell>
                        <StatusBadge status={getStatusColor(metric.trend)}>
                          {metric.trend.charAt(0).toUpperCase() + metric.trend.slice(1)}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Button style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>History</Button>
                          <Button primary style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}>Details</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </Card>
          </TabContent>
        </TabContainer>

        <Card style={{ marginTop: '2rem' }}>
          <CardTitle>Feedback Loop Process</CardTitle>
          <CardContent>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '1.5rem',
              padding: '0 2rem'
            }}>
              {/* Collect */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <div style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  background: 'rgba(0, 102, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  <div style={{ fontSize: '2rem' }}>⟲</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Collect</div>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textAlign: 'center', maxWidth: '120px' }}>
                  Gather feedback from users and systems
                </div>
              </div>

              {/* Arrow */}
              <div style={{ fontSize: '2rem', color: 'var(--primary)' }}>→</div>

              {/* Analyze */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <div style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  background: 'rgba(0, 102, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  <div style={{ fontSize: '2rem' }}>⟲</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Analyze</div>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textAlign: 'center', maxWidth: '120px' }}>
                  Identify patterns and root causes
                </div>
              </div>

              {/* Arrow */}
              <div style={{ fontSize: '2rem', color: 'var(--primary)' }}>→</div>

              {/* Improve */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <div style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  background: 'rgba(0, 102, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  <div style={{ fontSize: '2rem' }}>⟲</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Improve</div>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textAlign: 'center', maxWidth: '120px' }}>
                  Implement changes to agents and systems
                </div>
              </div>

              {/* Arrow */}
              <div style={{ fontSize: '2rem', color: 'var(--primary)' }}>→</div>

              {/* Measure */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <div style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  background: 'rgba(0, 102, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  <div style={{ fontSize: '2rem' }}>⟲</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>Measure</div>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textAlign: 'center', maxWidth: '120px' }}>
                  Track performance gains and validate improvements
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </PageContainer>
  );
};

export default FeedbackLoops;
